/**
 * 用户信息管理模块
 * 提供用户信息的获取、更新等功能，支持sessionStorage缓存
 */

class UserProfileAPI {
  constructor() {
    this.baseURL = window.API_BASE_URL; // 使用全局baseURL变量
    this.userData = null;
    this.STORAGE_KEY = 'userProfileData';
    this.STORAGE_TIMESTAMP_KEY = 'userProfileTimestamp';
    this.CACHE_DURATION = 30 * 60 * 1000; // 30分钟缓存
  }

  /**
   * 获取用户信息（默认强制刷新）
   * @param {boolean} forceRefresh - 是否强制刷新，默认为 false（优先使用缓存）
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async getUserProfile(forceRefresh = true) {
    // 如果不强制刷新，先尝试从sessionStorage读取
    if (!forceRefresh) {
      const cachedData = this.getCachedUserData();
      if (cachedData) {
        this.userData = cachedData;
        return { code: 200, message: 'success', data: cachedData };
      }
    }

    // 从API获取数据
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    const response = await fetch(`${this.baseURL}/authority/account/profile`, {
      method: 'GET',
      headers: {
        'X-Client-Type': 'web',
        'Authorization': `Bearer ${accessToken}`,
        'Refresh-token': refreshToken
      }
    }).catch(error => null);

    if (!response) {
      // 尝试从缓存获取数据
      const cachedData = this.getCachedUserData();
      if (cachedData) {
        this.userData = cachedData;
        return { code: 200, message: 'success', data: cachedData };
      }
      return { code: 0, message: 'Network error, please check your connection' };
    }

    // 401 由全局拦截器统一处理（global.js），这里不再重复处理
    // 如果返回 401，全局拦截器会显示 toast 并跳转到登录页
    
    const result = await response.json().catch(() => null);

    if (!result) {
      // 尝试从缓存获取数据
      const cachedData = this.getCachedUserData();
      if (cachedData) {
        this.userData = cachedData;
        return { code: 200, message: 'success', data: cachedData };
      }
      return { code: 0, message: 'Invalid response format' };
    }

    // 如果成功，缓存数据
    if (result.code === 200) {
      this.userData = result.data;
      this.cacheUserData(result.data);
      this.setPasswordModeFromUserData(result.data);
    } else {
      // 检查是否是用户不存在的错误
      if (result.code === 500 && result.message != null && result.message.includes('session has expired')) {
        // 清除 token 和缓存
        if (window.authAPI) {
          window.authAPI.clearAllTokens();
        }
        this.clearCachedUserData();
        // 重定向到登录页面
        setTimeout(() => {
          window.location.href = window.withSitePrefix ? window.withSitePrefix('/pages/login') : '/pages/login';
          if (window.showToast && result.message) {
            window.showToast(result.message, 'error', 3000);
          }
        }, 100);
        
        // 返回错误结果
        return result;
      }
      
      // 失败时尝试从缓存获取数据
      const cachedData = this.getCachedUserData();
      if (cachedData) {
        this.userData = cachedData;
        return { code: 200, message: 'success', data: cachedData };
      }
    }

    // 直接返回后端原始响应对象
    return result;
  }



  /**
   * 强制刷新用户数据（用于测试）
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async forceRefreshData() {
    // 强制从API获取最新数据
    const result = await this.getUserProfile(true);
    return result;
  }

  /**
   * 从sessionStorage获取缓存的用户数据
   * @returns {Object|null} 缓存的用户数据
   */
  getCachedUserData() {
    try {
      const cachedData = sessionStorage.getItem(this.STORAGE_KEY);
      const timestamp = sessionStorage.getItem(this.STORAGE_TIMESTAMP_KEY);

      if (!cachedData || !timestamp) {
        return null;
      }

      const now = Date.now();
      const cacheTime = parseInt(timestamp);

      // 检查缓存是否过期
      if (now - cacheTime > this.CACHE_DURATION) {
        this.clearCachedUserData();
        return null;
      }

      return JSON.parse(cachedData);
    } catch (error) {
      console.error('Error reading cached user data:', error);
      return null;
    }
  }

  /**
   * 缓存用户数据到sessionStorage
   * @param {Object} userData - 用户数据
   */
  cacheUserData(userData) {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(userData));
      sessionStorage.setItem(this.STORAGE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Error caching user data:', error);
    }
  }

  /**
   * 清除缓存的用户数据
   */
  clearCachedUserData() {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
      sessionStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
    } catch (error) {
      console.error('Error clearing cached user data:', error);
    }
  }

  /**
   * 根据用户信息设置passwordMode
   * @param {Object} userData - 用户数据
   */
  setPasswordModeFromUserData(userData) {
    try {
      // 检查是否为三方用户（根据registerSource判断）
      const isThirdPartyUser = userData.thirdPartyUser && userData.thirdPartyUser !== 1;
      if (isThirdPartyUser) {
        // 三方用户 - 设置passwordMode为add
        sessionStorage.setItem('passwordMode', 'add');
      } else {
        // 普通用户 - 设置passwordMode为change
        sessionStorage.setItem('passwordMode', 'change');
      }
    } catch (error) {
      console.error('设置passwordMode失败:', error);
    }
  }

  /**
   * 更新用户信息（统一接口）
   * @param {Object} updateData - 要更新的用户信息，对应 UpdateUserRequest
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async updateUserProfile(updateData) {
  
    const requestData = {
      firstName: updateData.firstName || null,
      lastName: updateData.lastName || null,
      email: updateData.email || updateData.userEmail || null,
      userPhone: updateData.userPhone || null,
      enableNotification: updateData.enableNotification || null,
      password: updateData.password || null,
    };
  
    const response = await fetch(`${this.baseURL}/authority/account/change-profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Type': 'web',
        'Authorization': `Bearer ${this.getAccessToken()}`,
        'Refresh-token': this.getRefreshToken()
      },
      body: JSON.stringify(requestData)
    }).catch(error => null);
  
    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    // 解析响应 JSON
    const result = await response.json().catch(() => null);
    
    
    // 检查 JSON 解析是否成功：如果响应体为空、格式错误或不是 JSON，result 会是 null
    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }


    // 更新成功后，重新获取最新的用户信息
    if (result.code === 200) {
      await this.getUserProfile(true); // 强制刷新
    }

    // 直接返回后端原始响应对象
    return result;
  }

  /**
   * 更新邮件偏好设置（使用统一接口）
   * @param {number|boolean} enableNotification - 通知开关状态 (1 启用 | 2 禁用 | true/false)
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async updateEmailPreferences(enableNotification) {
    // 参数验证：必须是 1(启用) 或 2(禁用)
    if (enableNotification !== 1 && enableNotification !== 2) {
      return { code: 0, message: 'enableNotification 必须为 1(启用) 或 2(禁用)' };
    }

    // 使用统一的更新接口
    const updateData = {
      enableNotification: enableNotification
    };

    const result = await this.updateUserProfile(updateData);

    if (result.code !== 200) {
      console.error('更新邮件偏好设置失败:', result.message);
    }

    return result;
  }

  /**
   * 获取当前用户数据（优先从缓存读取）
   * @returns {Object|null} 用户数据
   */
  getCurrentUserData() {
    if (this.userData) {
      return this.userData;
    }

    // 尝试从缓存读取
    const cachedData = this.getCachedUserData();
    if (cachedData) {
      this.userData = cachedData;
      return cachedData;
    }

    return null;
  }

  /**
   * 通用的 cookie 获取方法
   * @param {string} key - cookie 键名
   * @returns {string} cookie 值
   */
  _getFromCookie(key) {
    try {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === key) {
          return value || '';
        }
      }
      return '';
    } catch (error) {
      return '';
    }
  }

  /**
   * 获取 accessToken（直接从 cookie 读取）
   * @returns {string} accessToken
   */
  getAccessToken() {
    return this._getFromCookie('accessToken');
  }

  /**
   * 获取 refreshToken（直接从 cookie 读取）
   * @returns {string} refreshToken
   */
  getRefreshToken() {
    return this._getFromCookie('refreshToken');
  }

  /**
   * 格式化用户全名
   * @param {Object} userData - 用户数据
   * @returns {string} 格式化后的全名
   */
  formatFullName(userData) {
    if (!userData) return '';

    const firstName = userData.firstName || '';
    const lastName = userData.lastName || '';

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else {
      return 'Click to add';
    }
  }

  /**
   * 检查通知是否启用
   * @param {Object} userData - 用户数据
   * @returns {boolean} 是否启用通知
   */
  isNotificationEnabled(userData) {
    return userData?.enableNotification === 1;
  }

  /**
   * 初始化用户数据（移动端入口函数）
   * 在移动端首次进入时调用，获取并缓存用户信息
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async initializeUserData() {
    // 强制从API获取最新数据
    const result = await this.getUserProfile(true);

    if (result.code !== 200) {
      console.error('Failed to initialize user data:', result.message);
    }

    return result;
  }

  /**
   * 更新页面显示（获取用户数据并更新显示）
   * @param {Object} updateFunctions - 更新函数对象
   */
  async updatePageDisplay(updateFunctions = {}) {
    const result = await this.getUserProfile();

    if (result.code !== 200) {
      console.warn('No user data available for display update');
      return;
    }
    
    if (!result.data) {
      console.warn('No user data available for display update');
      return;
    }

    const userData = result.data;

    // 执行各个更新函数
    if (updateFunctions.updateGreeting) {
      updateFunctions.updateGreeting(userData);
    }

    if (updateFunctions.updatePersonalInfo) {
      updateFunctions.updatePersonalInfo(userData);
    }

    if (updateFunctions.updateEmailPreferences) {
      updateFunctions.updateEmailPreferences(userData);
    }

    if (updateFunctions.updateAccountSecurity) {
      updateFunctions.updateAccountSecurity(userData);
    }

    if (updateFunctions.updateAvatar) {
      updateFunctions.updateAvatar(userData);
    }
  }

  /**
   * 更新用户姓名
   * @param {string} firstName - 名字
   * @param {string} lastName - 姓氏
   * @returns {Promise<Object>} 更新结果
   */
  async updateUserName(firstName, lastName) {
    return await this.updateUserProfile({
      firstName: firstName,
      lastName: lastName
    });
  }

  /**
   * 更新用户邮箱
   * @param {string} userEmail - 邮箱地址
   * @returns {Promise<Object>} 更新结果
   */
  async updateUserEmail(userEmail) {
    return await this.updateUserProfile({
      email: userEmail
    });
  }

  /**
   * 安全获取当前用户ID（尽量从已缓存的用户数据中获取）
   * @returns {string|number|undefined}
   */
  getCurrentUserId() {
    try {
      const data = this.getCurrentUserData && this.getCurrentUserData();
      if (data) {
        return data.userId || data.id || data.uid || data.user_id;
      }
      // 兜底：尝试从本地存储获取（如果业务侧有存）
      const fromSession = sessionStorage.getItem('userId');
      if (fromSession) return fromSession;
      const fromLocal = localStorage.getItem('userId');
      if (fromLocal) return fromLocal;
    } catch (_) { }
    return undefined;
  }

  /**
   * 验证邮箱有效性
   * @param {string} email - 要验证的邮箱地址
   * @param {number} type - 场景：1 login 2 register 3 change email 4 forget password  5 reset password 
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async checkEmailValidity(email, type = 1) {
    const emailParam = email || '';

    const sceneType = {
      1: 'LOGIN',
      2: 'REGISTER'
    }
    // 构建请求体
    const requestBody = {
      email: emailParam,
      scene: sceneType[type] ?? ''
    };

    // 根据 type 决定是否需要 token
    // type=3(change email) 和 type=4(forget password) 需要 token
    const needsToken = type === 3 || type === 4;
    const accessToken = needsToken ? this.getAccessToken() : '';
    const refreshToken = needsToken ? this.getRefreshToken() : '';
    const response = await fetch(`${this.baseURL}/authority/account/check`, {
      method: 'POST',
      headers: {
        'X-Client-Type': 'web',
        'Content-Type': 'application/json',
        'Authorization': accessToken ? `Bearer ${accessToken}` : '',
        'Refresh-token': refreshToken
      },
      body: JSON.stringify(requestBody)
    }).catch(error => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    // 直接返回后端原始响应对象
    return result;
  }


}

// 创建全局实例
window.userProfileAPI = new UserProfileAPI();

// 测试方法 - 在浏览器控制台中使用
window.testUserProfile = {
  // 强制刷新用户数据
  forceRefresh: () => window.userProfileAPI.forceRefreshData(),

  // 获取当前用户数据（推荐使用 getUserProfile）
  getCurrentData: () => window.userProfileAPI.getCurrentUserData(),

  // 获取用户数据（推荐方法）
  getUserData: () => window.userProfileAPI.getUserProfile(),

  // 清除缓存
  clearCache: () => window.userProfileAPI.clearCachedUserData(),

  // 刷新用户数据
  refresh: () => window.userProfileAPI.getUserProfile(true),

  // 显示当前用户数据
  showCurrentData: () => {
    const data = window.userProfileAPI.getCurrentUserData();
    return data;
  },

  // 验证邮箱有效性
  checkEmail: (email) => window.userProfileAPI.checkEmailValidity(email)
};

// 导出模块（如果使用模块系统）
if (typeof module !== 'undefined' && module.exports) {
  module.exports = UserProfileAPI;
} 
